import { create } from 'axios';
import z from 'zod/v4';
import { axiosConciseErrorInterceptor } from '../util/axios.js';
import { retrying } from '../util/retry.js';
// Klimadatastyrelsen's replacement for DAWA: https://confluence.kds.dk/pages/viewpage.action?pageId=234782998
// KDS asks every client to send this shared token until they ship user management.
const TOKEN = 'adressevaelger123';
const client = create({
    baseURL: 'https://adressevaelger.dk',
    params: { token: TOKEN },
    timeout: 5000,
});
client.interceptors.response.use((res) => res, axiosConciseErrorInterceptor);
const get = retrying(async (url, params) => (await client.get(url, { params })).data);
// Street name results carry no id
const SearchResponseSchema = z.object({
    fund: z.array(z.object({
        id: z.string().optional(),
        type: z.string(),
        titel: z.string(),
        vejnavn: z.string().optional(),
        husnummer: z.string().optional(),
        postnr: z.string().optional(),
        postdistrikt: z.string().optional(),
    })),
});
const MAX_SEARCH_LENGTH = 73;
const HusnummerSchema = z.object({
    id_lokalid: z.string(),
    vejnavn: z.string(),
    husnummertekst: z.string(),
    adgangspunkt: z.object({
        koordinater: z.object({ x: z.number(), y: z.number() }),
    }),
    postnummer: z.object({ postnr: z.string(), navn: z.string() }),
    navngivenvejkommunedel: z.object({
        kommune: z.string(),
        vejkode: z.string(),
    }),
});
const AdresseSchema = z.object({
    id_lokalid: z.string(),
    etagebetegnelse: z.string().nullable(),
    doerbetegnelse: z.string().nullable(),
    husnummer: HusnummerSchema,
});
const VaskResponseSchema = z.object({
    vaskestatus: z.object({ kode: z.number() }),
    vaskeresultat: z.object({ adresse_id_lokalid: z.string().nullable() }),
});
const toAccessAddress = (husnummer) => ({
    id: husnummer.id_lokalid,
    street: husnummer.vejnavn,
    houseNumber: husnummer.husnummertekst,
    zipcode: husnummer.postnummer.postnr,
    city: husnummer.postnummer.navn,
    municipalityCode: husnummer.navngivenvejkommunedel.kommune,
    streetCode: husnummer.navngivenvejkommunedel.vejkode,
    easting: husnummer.adgangspunkt.koordinater.x,
    northing: husnummer.adgangspunkt.koordinater.y,
});
export const dkAddressLine = ({ accessAddress, floor, door }) => {
    const unit = [floor !== null ? `${floor}.` : null, door]
        .filter((part) => part !== null)
        .join(' ');
    return `${accessAddress.street} ${accessAddress.houseNumber}${unit !== '' ? `, ${unit}` : ''}`;
};
export const formatDKAddress = (address) => `${dkAddressLine(address)}, ${address.accessAddress.zipcode} ${address.accessAddress.city}`;
const toNarrowing = (result) => {
    const { titel, vejnavn, husnummer, postnr, postdistrikt } = result;
    if (vejnavn !== undefined && husnummer !== undefined) {
        const text = `${vejnavn} ${husnummer}, `;
        return {
            text,
            street: vejnavn,
            district: titel.startsWith(text) ? titel.slice(text.length) : null,
        };
    }
    if (vejnavn !== undefined &&
        postnr !== undefined &&
        postdistrikt !== undefined) {
        return {
            text: `${vejnavn} `,
            street: vejnavn,
            district: `${postnr} ${postdistrikt}`,
        };
    }
    return { text: `${titel} `, street: titel, district: null };
};
export const searchDKAddresses = async (text, district) => {
    // The search has no postal code filter, but ranks by the district in the text
    const searchText = district === null ? text : `${text.replace(/[\s,]+$/, '')}, ${district}`;
    if (searchText.length > MAX_SEARCH_LENGTH) {
        return [];
    }
    const { fund } = SearchResponseSchema.parse(await get('/adresser/soeg', { tekst: searchText }));
    return fund.map((result) => result.type === 'adresse' && result.id !== undefined
        ? { title: result.titel, addressId: result.id }
        : {
            title: result.titel,
            addressId: null,
            narrowing: toNarrowing(result),
        });
};
export const getDKAddress = async (id) => {
    const { adresse } = z
        .object({ adresse: AdresseSchema })
        .parse(await get(`/adresser/${encodeURIComponent(id)}`));
    return {
        id: adresse.id_lokalid,
        floor: adresse.etagebetegnelse,
        door: adresse.doerbetegnelse,
        accessAddress: toAccessAddress(adresse.husnummer),
    };
};
export const getDKAccessAddress = async (id) => {
    const { husnummer } = z
        .object({ husnummer: HusnummerSchema })
        .parse(await get(`/husnumre/${encodeURIComponent(id)}`));
    return toAccessAddress(husnummer);
};
const washDKAddress = async (text) => {
    const { vaskestatus, vaskeresultat } = VaskResponseSchema.parse(await get('/vask/', { adresse: text }));
    // Positive codes are (near-)exact matches, negative codes are failures
    return vaskestatus.kode > 0 ? vaskeresultat.adresse_id_lokalid : null;
};
const normalizeAddressText = (text) => text.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
// e.g. "12a|8000", so a lone near miss like 12 → 12A isn't taken for the address
const houseNumberAndZipcode = (text) => {
    const tokens = text.toLowerCase().split(/[\s,.]+/);
    const houseNumber = tokens.find((token) => /^\d+[a-z]?$/.test(token));
    const zipcode = tokens.filter((token) => /^\d{4}$/.test(token)).pop();
    return houseNumber === undefined || zipcode === undefined
        ? null
        : `${houseNumber}|${zipcode}`;
};
// Adressevask rejects abbreviated street names ("Otto Brandenburgs V"), doors
// without floor ("10, 31.") and "KL.", which the search endpoint handles
const searchDKAddressId = async (text) => {
    if (text.length > MAX_SEARCH_LENGTH) {
        return null;
    }
    const { fund } = SearchResponseSchema.parse(await get('/adresser/soeg', { tekst: text }));
    const addresses = fund.filter((result) => result.type === 'adresse');
    const exactMatches = addresses.filter((result) => normalizeAddressText(result.titel) === normalizeAddressText(text));
    const inputHouseNumberAndZipcode = houseNumberAndZipcode(text);
    const match = addresses.length === 1 &&
        inputHouseNumberAndZipcode !== null &&
        houseNumberAndZipcode(addresses[0].titel) === inputHouseNumberAndZipcode
        ? addresses[0]
        : exactMatches.length === 1
            ? exactMatches[0]
            : null;
    return match?.id ?? null;
};
/** Returns the DAR address id when the address can be identified unambiguously. */
export const findDKAddressId = async (text) => (await washDKAddress(text)) ?? (await searchDKAddressId(text));
/**
 * Resolves the access address (husnummer) of a free-text address. Adressevask
 * rejects addresses in multi-unit buildings that lack floor or door, so those
 * fall back to a husnummer search that must be unambiguous.
 */
export const findDKAccessAddress = async (text) => {
    const addressId = await findDKAddressId(text);
    if (addressId !== null) {
        return (await getDKAddress(addressId)).accessAddress;
    }
    if (text.length > MAX_SEARCH_LENGTH) {
        return null;
    }
    const { fund } = SearchResponseSchema.parse(await get('/husnumre/soeg', { tekst: text }));
    const [match] = fund;
    if (fund.length !== 1 || match.id === undefined) {
        return null;
    }
    return getDKAccessAddress(match.id);
};
//# sourceMappingURL=adressevaelger.js.map